import { fabric as fabricBase } from "fabric"
import fabricEnhancer from "../fabric"
import axios from "axios"

import { canvasEvent } from "../events"

declare global {
  interface Window {
    fbq?: (track: string, event: string) => void
  }
}
const { fbq } = window

const { pipe, extendingMethods, extendingUtils, history, sprayBrush } =
  fabricEnhancer

let fabric = pipe(
  extendingUtils,
  extendingMethods,
  history(canvasEvent),
  sprayBrush,
)(fabricBase)

export default (
  csrfName,
  csrfToken,
  siteUrl,
  fullPath,
  formHandle,
  campaignCategoryId,
) => ({
  canvasId: "artIs",
  defaultSize: { width: 1680, height: 882 },
  outputWidth: 1680,
  entryId: 0,
  canvas: null,
  artist: "Anonymous",
  artistCoded: "anonymous",
  originalSize: {
    width: 0,
    height: 0,
  },
  settings: {
    preserveObjectStacking: true,
    isDrawingMode: false,
    width: 100,
    height: 100,
  },
  settingsExport: {
    format: "jpeg",
    quality: 0.975,
  },
  // Default values reflect when the canvas' width in 1680px
  sprayWidth: 110,
  defaultSprayWidth: 110,
  defaultBrushWidth: 10,
  defaultFontSize: 116,
  scaleX: 1,
  stateUndo: false,
  text: "Art",
  textLayer: 0,
  cText: null,
  fontFamily:
    "NB_Akademie, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, sans-serif, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif",
  color: "#000000",
  activeObject: null,
  selectedTool: String,
  selectedPencil: 1,
  palette: ["#000000", "#FFFFFF", "#A5FFC8", "#6088FF", "#EDEAE7"],
  swatch: 0,
  bgImageList: ["/assets/img/squiggla-canvas-1.png"],
  currentBg: 0,
  isSubmittingDisabled: true,
  isSubmitting: false,
  submittingSuccess: false,
  submittingError: false,
  imageUrlBlob: "",
  showHelpModal: false,
  showSubmissionModal: false,
  showNewsletterForm: false,
  showShareMessage: false,
  preventKeyListener: false,

  get ratio() {
    const { width, height } = this.defaultSize
    return Math.round((width / height) * 100) / 100
  },
  get paddingBottom() {
    const { width, height } = this.defaultSize
    return `padding-bottom: ${(height / width) * 100}%`
  },
  initFabric() {
    this.canvas = Alpine.raw(new fabric.Canvas(this.canvasId, this.settings))
    this.canvas.on({
      "selection:created": this.setObjectActive.bind(this),
      "selection:updated": this.setObjectActive.bind(this),
      "selection:cleared": this.setObjectActive.bind(this),
      // History related actions
      "object:added": () => this.onCanvasChange(false),
      "object:removed": () => this.onCanvasChange(false),
      "object:modified": () => this.onCanvasChange(false),
    })
  },
  addText(text) {
    const { scaleX, defaultFontSize, defaultSize } = this

    const sizeRatio =
      Math.round((defaultFontSize / defaultSize.width) * 1000) / 1000
    const size = Math.round(scaleX * defaultSize.width * sizeRatio)
    this.selectedTool = "Select"
    if (!this.text && !text) return
    if (!this.text) this.text = text
    if (this.cText) {
      this.cText.set({ text: this.text })
      this.canvas.requestRenderAll()
    } else if (this.activeObject && this.activeObject.type === "text") {
      this.activeObject.set({ text: this.text })
      this.canvas.requestRenderAll()
    } else {
      this.textLayer += 1
      const fontSize = 26
      const position = {
        top: Math.round(200 * scaleX),
        left: Math.round(200 * scaleX),
      }

      const { width } = this.canvas
      if (width) {
        position.left = width / 2 - Math.round(100 * scaleX)
        position.top =
          Math.round(size * 0.2) + this.textLayer * Math.round(size / 1.2)
      }
      this.cText = new fabric.IText(this.text, {
        fontSize: size,
        left: position.left,
        top: position.top,
        fill: this.color,
        fontWeight: 900,
        fontFamily: this.fontFamily,
      })
      this.canvas.add(this.cText)
    }
    this.canvas.isDrawingMode = false
    this.cText = null
  },
  addImage(e) {
    const canvas = Alpine.raw(this.canvas)
    let { ratio, isDrawingMode } = this
    const { width: cW, height: cH } = canvas
    const reader = new FileReader()
    reader.onload = event => {
      const imgObj = new Image()
      imgObj.src = event.target.result as string
      imgObj.onload = function () {
        const image = new fabric.Image(imgObj)
        const { width: iW, height: iH } = image

        image.hasRotatingPoint = false

        const imageRatio = Math.round(iW / iH)

        // d = display
        const dW = Math.round(cW / 2)
        const dH = Math.round(cH / 2)

        if (imageRatio > ratio) {
          // Landscape // square
          if (iW > dW) image.scaleToWidth(dW)
        } else {
          // Portrait
          if (iH > dH) image.scaleToHeight(dH)
        }

        canvas.centerObject(image)
        canvas.add(image)
        canvas.isDrawingMode = false
        // canvas.setActiveObject(image)
        canvas.requestRenderAll()
        e.target.value = ""
      }
    }
    reader.readAsDataURL(e.target.files[0])
    this.selectedTool = "Select"
  },
  initHistory() {
    this.canvas.historyInit()
    setTimeout(() => {
      canvasEvent.on("undo", () => this.onCanvasChange(true))
    })
  },
  initBrush(brush) {
    const { currentTool } = this
    this.canvas.isDrawingMode = true
    this.canvas.freeDrawingBrush = new fabric[brush](this.canvas)
  },
  updateBrush(settings) {
    Object.keys(settings).forEach(key => {
      this.canvas.freeDrawingBrush[key] = settings[key]
    })
  },
  usePencils() {
    this.usePencil(this.selectedPencil)
  },
  usePencil(size) {
    this.selectedTool = "Pencils"
    this.selectedPencil = size
    this.initBrush("PencilBrush")
    this.updateBrush({
      width: size,
      color: this.hex2Rgba(0.8)(this.color),
    })
  },
  updateSprayWidth() {
    const { defaultSprayWidth, defaultSize, scaleX } = this
    const widthRatio =
      Math.round((defaultSprayWidth / defaultSize.width) * 1000) / 1000
    this.sprayWidth = Math.round(scaleX * defaultSize.width * widthRatio)
  },
  useSpray() {
    this.initBrush("SprayBrush")
    this.selectedTool = "Spray"
    this.canvas.freeDrawingBrush.width = this.sprayWidth
    this.canvas.freeDrawingBrush.changeColor(this.color)
    this.canvas.freeDrawingBrush.opacity = 1
  },
  useBrush() {
    const { defaultBrushWidth, defaultSize, scaleX } = this
    const widthRatio =
      Math.round((defaultBrushWidth / defaultSize.width) * 1000) / 1000
    const brushWidth = Math.round(scaleX * defaultSize.width * widthRatio)
    this.selectedTool = "Brush"
    this.initBrush("PencilBrush")
    this.updateBrush({
      width: brushWidth > 5 ? brushWidth : 5,
      strokeLineCap: "butt",
      color: this.hex2Rgba(0.8)(this.color),
    })
  },
  onCanvasChange(isUndo) {
    // Empty Canvas checking
    const objects = this.canvas.getObjects()
    if (objects.length) this.isSubmittingDisabled = false
    // Undo
    if (!isUndo) this.canvas.historySaveAction()
    if (this.canvas.historyUndo.length <= 1) {
      this.stateUndo = false
    } else {
      this.stateUndo = true
    }
  },
  setOriginalSize() {
    this.originalSize.width = this.settings.width
    this.originalSize.height = this.settings.height
  },
  setSelect() {
    this.selectedTool = "Select"
    this.canvas.isDrawingMode = false
  },
  setBackgroundImage() {
    const { canvas, bgImageList } = this
    const bgImagesLength = bgImageList.length
    if (this.currentBg >= bgImagesLength) {
      this.currentBg = 0
    }

    if (!bgImageList[this.currentBg]) {
      canvas.backgroundImage = null
      canvas.requestRenderAll()
    } else {
      fabric.Image.fromURL(
        bgImageList[this.currentBg],
        img => {
          if (img.width === 0) return
          img.scaleToWidth(canvas.width)
          img.scaleToHeight(canvas.height)
          canvas.setBackgroundImage(img)
          this.onCanvasChange(false)
          canvas.requestRenderAll()
        },
        {
          crossOrigin: "anonymous",
        },
      )
    }
    this.currentBg += 1
  },
  setColor() {
    const paletteLength = this.palette.length
    this.swatch += 1
    if (this.swatch >= paletteLength) {
      this.swatch = 0
    }
    this.color = this.palette[this.swatch]
  },
  setObjectActive() {
    this.activeObject = this.canvas.getActiveObject()
    if (!this.activeObject) return
    this.activeObject.set({
      borderColor: "#191919",
      cornerColor: "#191919",
      lockUniScaling: true,
    })
  },
  setKeyDown(event) {
    if (this.preventKeyListener) return
    // https://keycode.info/
    switch (event.keyCode) {
      case 8: // delete
        return this.toDelete()
      case 16:
        return
      default:
        return
    }
  },
  setKeyUp() {
    if (this.preventKeyListener) return
    // @ts-ignore
    switch (event.keyCode) {
      case 16: // shiftdown
        return
      default:
        return
    }
  },
  setCanvasSize() {
    if (!this.$refs) return
    if (!this.$refs.container) return
    if (!this.$refs.container.clientWidth) return
    if (!this.$refs.container.clientHeight) return
    this.settings.height = this.$refs.container.clientHeight
    this.settings.width = this.$refs.container.clientWidth
    this.scaleX =
      Math.round((this.settings.width / this.defaultSize.width) * 1000) / 1000
  },
  setOverlayImage() {
    const { canvas, settings } = this
    const svgWidth = settings.width / 5
    const svgHeight = svgWidth * 0.17
    const padding = 16
    const left = canvas.width - svgWidth - padding
    const top = canvas.height - svgHeight - padding

    fabric.loadSVGFromString(
      `<svg fill="none" viewBox="0 0 278 50"><path fill="#000" d="M2.086 21.485v5.834H.226V13.402h5.31c2.947 0 4.827 1.376 4.827 4.032 0 2.616-1.88 4.05-4.827 4.05h-3.45Zm0-1.61h3.353c1.978 0 3.082-.871 3.082-2.441 0-1.57-1.105-2.423-3.082-2.423H2.086v4.865Zm18.388 2.385c0 3.14-1.938 5.273-4.71 5.273-2.772 0-4.71-2.133-4.71-5.273 0-3.14 1.938-5.272 4.71-5.272 2.772 0 4.71 2.132 4.71 5.272Zm-7.656 0c0 2.307 1.163 3.78 2.946 3.78 1.783 0 2.946-1.473 2.946-3.78 0-2.326-1.163-3.8-2.946-3.8-1.783 0-2.946 1.474-2.946 3.8Zm14.512-5.078h1.473l2.094 7.56 2.113-7.56h1.744l-3.082 10.137h-1.531l-2.113-7.52-2.113 7.52h-1.531l-3.082-10.137h1.802l2.133 7.54 2.093-7.54Zm12.986 10.35c-2.81 0-4.748-2.151-4.748-5.272 0-3.024 1.919-5.272 4.671-5.272 2.83 0 4.323 2.151 4.323 4.962v.582h-7.328c.097 2.132 1.319 3.489 3.082 3.489 1.357 0 2.327-.737 2.637-1.939l1.531.543c-.62 1.803-2.151 2.908-4.168 2.908Zm-.096-9.052c-1.474 0-2.578.989-2.889 2.714h5.467c-.04-1.434-.814-2.714-2.578-2.714Zm11.766-1.337v1.764a3.99 3.99 0 0 0-.852-.078c-1.474 0-2.656 1.163-2.656 2.947v5.543h-1.745V17.182h1.745v1.725c.446-1.028 1.434-1.803 2.81-1.803.272 0 .524.02.698.039Zm5.556 10.39c-2.81 0-4.748-2.152-4.748-5.273 0-3.024 1.918-5.272 4.671-5.272 2.83 0 4.323 2.151 4.323 4.962v.582H54.46c.097 2.132 1.319 3.489 3.082 3.489 1.357 0 2.327-.737 2.637-1.939l1.531.543c-.62 1.803-2.151 2.908-4.168 2.908Zm-.096-9.053c-1.474 0-2.578.989-2.889 2.714h5.467c-.04-1.434-.814-2.714-2.578-2.714Zm10.235 9.053c-2.81 0-4.42-2.404-4.42-5.273 0-2.888 1.61-5.272 4.42-5.272 1.415 0 2.54.678 3.14 1.667v-5.253h1.745v13.917H70.82v-1.473c-.6 1.008-1.725 1.687-3.14 1.687Zm3.179-5.622c0-2.365-1.434-3.392-2.869-3.392-1.9 0-2.966 1.55-2.966 3.741 0 2.171 1.067 3.741 2.966 3.741 1.434 0 2.869-1.047 2.869-3.373v-.717Zm13.563 5.622c-1.415 0-2.54-.679-3.14-1.687v1.473h-1.745V13.402h1.745v5.253c.6-.989 1.725-1.667 3.14-1.667 2.81 0 4.42 2.384 4.42 5.272 0 2.869-1.61 5.273-4.42 5.273Zm-3.179-5.622v.717c0 2.326 1.435 3.373 2.869 3.373 1.9 0 2.966-1.57 2.966-3.74 0-2.191-1.066-3.742-2.966-3.742-1.434 0-2.869 1.027-2.869 3.392Zm13.337 6.513c-.582 1.532-1.376 2.5-3.14 2.5-.427 0-.64-.019-.95-.077v-1.512c.33.078.543.097.834.097.755 0 1.162-.271 1.57-1.28l.484-1.2-3.76-9.77h1.841l2.85 7.753 2.81-7.753h1.822l-4.361 11.242Z"/><path fill="#211D1D" d="m109 24.328 3.314-4.83c1.939 1.939 4.583 3.279 7.404 3.279 1.481 0 2.926-.6 2.926-1.904 0-.917-.846-1.516-2.115-2.01-1.551-.6-3.737-1.163-5.641-2.115-2.362-1.234-4.302-3.209-4.302-6.77 0-4.583 3.984-8.038 9.696-8.038 3.208 0 6.558 1.023 9.272 3.279l-3.067 5.006c-1.587-1.34-4.231-2.15-6.099-2.15-1.904 0-2.786.81-2.786 1.692 0 1.093 1.129 1.587 2.081 1.94 1.374.528 3.525 1.233 5.288 1.973 2.75 1.164 4.971 3.103 4.971 6.84 0 4.76-3.949 8.32-10.33 8.32-4.301 0-8.003-1.621-10.612-4.512ZM147.04 18.687c0-2.503-1.833-4.478-4.372-4.478-2.609 0-4.407 1.975-4.407 4.478 0 2.468 1.798 4.478 4.407 4.478 2.539 0 4.372-2.01 4.372-4.478Zm6.17-9.766v27.465h-6.241v-9.66c-1.339 1.269-3.137 2.15-5.57 2.15-5.535 0-9.449-4.618-9.449-10.26 0-5.64 3.878-10.188 9.484-10.188 2.433 0 4.266.951 5.606 2.256V8.921h6.17ZM156.522 19.885V8.92h6.241v11.036c0 1.868 1.163 3.243 3.102 3.243 1.975 0 3.138-1.375 3.138-3.243V8.92h6.241v10.965c0 5.817-3.597 8.99-9.379 8.99-5.747 0-9.343-3.172-9.343-8.99ZM178.591 28.382V8.921h6.241v19.461h-6.241Zm-.493-24.785c0-2.01 1.586-3.525 3.596-3.525s3.561 1.516 3.561 3.525c0 2.08-1.551 3.596-3.561 3.596-2.045 0-3.596-1.516-3.596-3.596ZM202.564 18.546c0-2.538-1.868-4.407-4.406-4.407-2.539 0-4.407 1.834-4.407 4.407 0 2.61 1.868 4.513 4.407 4.513 2.538 0 4.406-1.939 4.406-4.513Zm5.888-9.625v17.593c0 6.311-4.83 10.119-11.07 10.119-3.49 0-6.875-1.199-9.202-2.926l2.257-4.548c1.727 1.128 4.089 2.22 6.698 2.22 3.667 0 5.394-2.044 5.394-4.477v-.423c-1.233 1.164-2.996 1.94-5.429 1.94-5.852 0-9.625-4.161-9.625-9.943 0-6.064 4.337-10.048 9.907-10.048 2.362 0 4.16.881 5.465 2.115V8.921h5.605ZM226.22 18.546c0-2.538-1.868-4.407-4.406-4.407-2.539 0-4.408 1.834-4.408 4.407 0 2.61 1.869 4.513 4.408 4.513 2.538 0 4.406-1.939 4.406-4.513Zm5.888-9.625v17.593c0 6.311-4.83 10.119-11.07 10.119-3.49 0-6.875-1.199-9.202-2.926l2.256-4.548c1.728 1.128 4.09 2.22 6.699 2.22 3.667 0 5.394-2.044 5.394-4.477v-.423c-1.234 1.164-2.996 1.94-5.429 1.94-5.853 0-9.625-4.161-9.625-9.943 0-6.064 4.336-10.048 9.907-10.048 2.362 0 4.16.881 5.465 2.115V8.921h5.605ZM235.421 28.382v-26.9h6.205v26.9h-6.205ZM259.5 18.616a4.499 4.499 0 0 0-4.513-4.513c-2.644 0-4.477 2.08-4.477 4.513 0 2.538 1.903 4.583 4.477 4.583 2.574 0 4.513-2.08 4.513-4.583Zm5.923-9.696v19.462h-5.853V26.69c-1.374 1.269-3.243 2.185-5.746 2.185-5.641 0-9.519-4.547-9.519-10.294 0-5.853 4.019-10.154 9.519-10.154 2.503 0 4.372.952 5.746 2.256V8.92h5.853ZM268.108 2.981v-.884h3.777v.884h-1.426v4.065h-.925V2.981h-1.426ZM272.884 2.097h.576l1.982 2.886 1.975-2.886H278v4.95h-.91V4.112l-1.501 2.17h-.301l-1.5-2.17v2.933h-.904V2.097ZM236.307 44.23c0-.77.613-1.368 1.4-1.368.739 0 1.352.598 1.352 1.369 0 .802-.613 1.384-1.352 1.384-.787 0-1.4-.582-1.4-1.384ZM246.531 41.038c0-1.243-.959-2.234-2.202-2.234-1.227 0-2.186.991-2.186 2.234 0 1.274.959 2.265 2.186 2.265 1.243 0 2.202-.991 2.202-2.265Zm-6.842.016c0-2.502 1.856-4.547 4.64-4.547 2.8 0 4.672 2.045 4.672 4.547 0 2.5-1.872 4.545-4.672 4.545-2.784 0-4.64-2.045-4.64-4.545ZM250.181 36.744h2.359v1.117c.567-.928 1.385-1.353 2.344-1.353.315 0 .708.079 1.07.205l-.205 2.36a3.346 3.346 0 0 0-1.085-.19c-1.132 0-2.014.677-2.014 2.548v3.933h-2.469v-8.62ZM263.048 40.99c0-1.274-.928-2.202-2.186-2.202s-2.202.928-2.202 2.203c0 1.29.944 2.249 2.202 2.249s2.186-.96 2.186-2.25Zm2.328-4.247v7.77c0 2.817-2.076 4.515-4.829 4.515-1.541 0-2.988-.535-4.026-1.29l.943-1.824c.771.519 1.841 1.006 2.989 1.006 1.699 0 2.564-.99 2.564-2.217v-.33c-.582.597-1.385 1.006-2.548 1.006-2.564 0-4.294-1.856-4.294-4.404 0-2.659 1.934-4.451 4.388-4.451 1.132 0 1.966.44 2.564 1.069v-.85h2.249Z"/></svg>`,
      (svgElements, options) => {
        const overlaySvg = new fabric.util.groupSVGElements(
          svgElements,
          options,
        )
        overlaySvg.set({
          top,
          left,
        })
        overlaySvg.scaleToWidth(settings.width / 5)
        canvas.setOverlayImage(overlaySvg, () => canvas.requestRenderAll())
      },
      null,
      { crossOrigin: "anonymous" },
    )
  },
  toUndo() {
    const preSelectedTool = this.selectedTool
    this.selectedTool = "Select"
    this.canvas.undo()
    this.selectedTool = preSelectedTool
  },
  toDelete() {
    if (!this.activeObject) return
    if (
      this.activeObject.type === "activeSelection" &&
      this.activeObject._objects &&
      Array.isArray(this.activeObject._objects)
    ) {
      this.activeObject._objects.forEach(obj => {
        this.canvas.remove(obj)
      })
    } else {
      this.canvas.remove(this.activeObject)
    }
    this.canvas.discardActiveObject()
  },
  toPost() {
    const artSectionUid = "a260e430-ee1d-4ec6-8e10-bcf3b53519de"
    // const campaignContactSectionUid = ""
    const outputScaleX =
      Math.round((this.outputWidth / this.settings.width) * 1000) / 1000
    this.resetSubmittingState()
    this.isSubmitting = true
    this.settingsExport.multiplier = outputScaleX

    let trimmedArtist = ""
    const artist = window.prompt("Please enter your name:")
    if (typeof artist !== "string") {
      this.isSubmitting = false
      return
    }
    if (artist) {
      trimmedArtist = artist
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, " ") // remove special characters
        .replace(/[ \t]+$/g, "") // remove trailing whitespace
        .replace(/\s+/g, "_") // replace whitespace to '_'
    }
    if (trimmedArtist) {
      this.artist = artist // For display name, e.g. my name 200
      this.artistCoded = trimmedArtist // For coded name, e.g. my_name_200
    }
    const imageData = this.canvas.toDataURL(this.settingsExport)

    const { format: imageFormat } = this.settingsExport
    axios
      .get(imageData, { responseType: "arraybuffer" })
      .then(response => {
        const blob = new Blob([response.data], {
          type: `image/${imageFormat}`,
        })
        this.imageUrlBlob = URL.createObjectURL(blob)
        const form = new FormData()

        form.append(csrfName, csrfToken)
        form.append("action", "guest-entries/save")
        form.append("sectionUid", artSectionUid)
        form.append("title", this.artist)
        form.append(
          "fields[image]",
          blob,
          `art-${this.artistCoded}.${imageFormat}`,
        )
        form.append("fields[campaignCategory][]", campaignCategoryId)
        return axios.post("/events/arts-month", form)
      })
      .then(({ data }) => {
        if (data.modelId) this.entryId = data.modelId
        this.isSubmitting = false
        this.submittingSuccess = true
        this.resetCanvas()
        this.showSubmissionModal = true
        this.showNewsletterForm = true
        if (fbq) fbq("track", "CompleteRegistration")

        setTimeout(() => {
          this.handleForm()
        }, 300)
      })
      .catch(err => {
        if (err && err.data && err.data.error) console.error(err.data.error)
        this.isSubmitting = false
        this.submittingError = true
      })
  },
  sendForward() {
    if (!this.activeObject) return
    this.canvas.bringForward(this.activeObject)
  },
  sendBackward() {
    if (!this.activeObject) return
    this.canvas.sendBackwards(this.activeObject)
  },
  resetSubmittingState() {
    this.submittingError = false
    this.submittingSuccess = false
    this.isSubmitting = false
    this.showSubmissionModal = false
    this.showShareMessage = false
    this.showNewsletterForm = false
    this.artist = "Anonymous"
    this.artistCoded = "anonymous"
  },
  resetCanvas() {
    const { canvas } = this
    // 01. remove all object on canvas, excludes: gb-image, bg-color, overlay-image, overlay-color
    canvas.remove(...canvas.getObjects())
    canvas.discardActiveObject()
    // 02. Reset background image
    this.currentBg = 0
    this.setBackgroundImage()
    // 03. Reset color
    // any number that bigger than the palette length
    this.swatch = this.palette.length + 1
    this.setColor()
    // 04, init Defaul pen tools
    this.usePencils()
  },
  hex2Rgba(opacity = 1) {
    const { color: defaultColor } = this
    return hex => {
      function toRgba(h) {
        let c
        c = h.substring(1).split("")
        if (c.length == 3) c = [c[0], c[0], c[1], c[1], c[2], c[2]]
        c = "0x" + c.join("")
        return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(
          ",",
        )},${opacity})`
      }
      if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        return toRgba(hex)
      }
      return toRgba(defaultColor)
    }
  },
  debounce(cb) {
    let timer
    return event => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(cb, 100, event)
    }
  },
  handleForm() {
    const form = document.querySelector(`[data-handle=${formHandle}]`)
    form.addEventListener("freeform-ajax-success", event => {
      setTimeout(() => {
        this.showNewsletterForm = false
        this.showShareMessage = true
      }, 1000)
    })
  },

  closeModal() {
    this.showSubmissionModal = false
    this.showNewsletterForm = false
    this.showShareMessage = false
  },

  init() {
    this.resetSubmittingState()
    this.$nextTick(() => {
      // The order matters
      this.setCanvasSize()
      this.initFabric()
      this.setOverlayImage()
      this.initHistory()
      this.setOriginalSize()
      this.setBackgroundImage()
      window.addEventListener("keydown", this.setKeyDown.bind(this))
      window.addEventListener("keyup", this.setKeyUp.bind(this))
      window.addEventListener("resize", this.setCanvasSize.bind(this))
      this.usePencils()
      // First Render
      this.canvas.requestRenderAll()
      this.canvas.setBackgroundColor("#FFF")
      this.updateSprayWidth()

      this.$watch("color", (newVal, oldVal) => {
        if (newVal === oldVal) return true
        this.updateBrush({ color: newVal })
      })
      this.$watch("activeObject", (newObject, oldObject) => {
        if (!newObject || !newObject.hasControls) return
        if (newObject.type === "image" && !newObject.customType) return
        if (newObject.type === "i-text") {
          newObject.on("editing:entered", () => {
            this.preventKeyListener = true
          })
          newObject.on("editing:exited", () => {
            this.preventKeyListener = false
          })
          return
        }
        newObject.hasControls = false
      })
      this.$watch("text", (newText, oldText) => {
        if (newText === oldText) return
      })
      this.$watch("settings", newSettings => {
        const { originalSize } = this
        const { width, height } = newSettings
        // const scaleRatio = Math.min(width, height);
        // Update Zoom
        const scaleX = Math.round((width / originalSize.width) * 1000) / 1000
        // const yZoom = Math.round((height / originalSize.heigth) * 1000) / 1000;
        this.canvas.setZoom(scaleX)
        // Get new Zoom
        const zoom = this.canvas.getZoom()
        // Apply new Zoom
        this.canvas.setWidth(originalSize.width * zoom)
        this.canvas.setHeight(originalSize.height * zoom)
        this.canvas.requestRenderAll()
      })
      this.$watch("imageUrlBlob", url => {
        canvasEvent.emit("update:imageUrlBlob", url)
      })
      this.$watch("artist", name => {
        canvasEvent.emit("update:artist", name)
      })
    })
  },

  destroy() {
    window.removeEventListener("keydown", this.setKeyDown.bind(this))
    window.removeEventListener("keyup", this.setKeyUp.bind(this))
    window.removeEventListener("resize", this.setCanvasSize.bind(this))
  },
})
